import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '@clerk/react';
import {
  AgentEventPayload,
  DoBoxRuleDecision,
  InboxEntry,
  InboxEntryReplyStatePayload,
  InboxEntryUnread,
  IntegrationAccountRemovedPayload,
  KnowledgeFavoritePayload,
  KnowledgePage,
  KnowledgeSpace,
  LocalDevicePresencePayload,
  MessagingPlatform,
  Notification,
  PersonActivityCreatedPayload,
  PersonActivityMigratedPayload,
  PersonActivityRemovedPayload,
  TaskGroup,
  TaskLabel,
  TaskSubgroup,
  Team,
  TeamInvite,
  TeamPersonPayload,
  UserCommunication,
  UserCommunicationRemoveBatchPayload,
  UserCommunicationRemovePayload,
  WebSocketAction,
  WebSocketEntityType,
  WebSocketMessage,
} from '@this+that/models';
import dayjs from 'dayjs';
import log from 'loglevel';

import { EventEmitter } from './EventEmitter';
import type { FlowGenerationJobResult } from './handlers';
import {
  createAgentEventMessageHandler,
  createDoBoxRuleDecisionMessageHandler,
  createFlowGenerationJobMessageHandler,
  createInboxEntryMessageHandler,
  createInboxEntryReplyStateMessageHandler,
  createInboxEntryUnreadMessageHandler,
  createIntegrationAccountMessageHandler,
  createKnowledgeFavoriteMessageHandler,
  createKnowledgePageMessageHandler,
  createKnowledgeSpaceMessageHandler,
  createLocalDevicePresenceMessageHandler,
  createNotificationMessageHandler,
  createPersonActivityMessageHandler,
  createPersonMessageHandler,
  createTaskGroupMessageHandler,
  createTaskLabelMessageHandler,
  createTaskSubgroupMessageHandler,
  createTeamInviteMessageHandler,
  createTeamMessageHandler,
  createUserCommunicationMessageHandler,
} from './handlers';
import {
  ConnectionState,
  WebSocketContext,
  WebSocketEventCallback,
  WebSocketEventType,
} from './WebSocketContext';
import { useIntegrationsStore } from '../../store';

// Missing config must not throw at module scope — that crashes every indirect
// importer (tests, unrelated components). It's surfaced at connect() time instead.
const WS_URL = import.meta.env.VITE_WEBSOCKET_BASE_URL;

// Centralized configuration with validation
const WS_CONFIG = {
  url: WS_URL ?? '',
  heartbeat: {
    interval: 1 * 60 * 1000, // 1 minute
    timeout: 8 * 60 * 1000, // 8 minutes (stale connection)
  },
  reconnect: {
    baseDelay: 1000, // 1 second
    maxDelay: 30 * 1000, // 30 seconds
    maxAttempts: 10, // Max attempts limit
    backoffMultiplier: 2,
  },
  longPauseThreshold: 2 * 60 * 1000, // 2 minutes
  connectionTimeout: 10 * 1000, // 10 seconds
} as const;

const getCurrentTimestamp = () => dayjs().valueOf();

export function shouldResyncAfterReconnect(
  lastDisconnectTime: number | null,
  staleSince: number | null,
  now: number,
  threshold: number,
): boolean {
  const disconnectedTooLong =
    lastDisconnectTime !== null && now - lastDisconnectTime > threshold;
  const staleTooLong = staleSince !== null && now - staleSince > threshold;
  return disconnectedTooLong || staleTooLong;
}

// Custom hook to create stable function reference that always calls the latest version
function useEventCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef<T>(fn);

  useEffect(() => {
    ref.current = fn;
  }, [fn]);

  return useCallback((...args: Parameters<T>) => {
    return ref.current(...args);
  }, []) as T;
}

// Custom hook to manage reconnection state
function useReconnectStrategy() {
  const attemptsRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);

  const calculateDelay = useCallback(() => {
    const { baseDelay, maxDelay, backoffMultiplier } = WS_CONFIG.reconnect;
    return Math.min(
      baseDelay * backoffMultiplier ** attemptsRef.current,
      maxDelay,
    );
  }, []);

  const reset = useCallback(() => {
    attemptsRef.current = 0;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback(
    (callback: () => void): void => {
      if (attemptsRef.current >= WS_CONFIG.reconnect.maxAttempts) {
        log.error('Max reconnection attempts reached');
        reset();
        return;
      }

      // Clear any existing timeout before scheduling a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const delay = calculateDelay();
      log.info(
        `Scheduling reconnect in ${delay}ms (attempt ${attemptsRef.current + 1})`,
      );

      timeoutRef.current = window.setTimeout(() => {
        attemptsRef.current += 1;
        callback();
      }, delay);
    },
    [calculateDelay, reset],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useMemo(() => ({ reset, scheduleNext }), [reset, scheduleNext]);
}

// Custom hook for heartbeat
function useHeartbeat(
  wsRef: RefObject<WebSocket | null>,
  onStaleConnection: (lastActivity: number) => void,
) {
  const intervalRef = useRef<number | null>(null);
  const lastActivityRef = useRef(getCurrentTimestamp());

  const updateActivity = useCallback(() => {
    lastActivityRef.current = getCurrentTimestamp();
  }, []);

  const isStale = useCallback(() => {
    return (
      getCurrentTimestamp() - lastActivityRef.current >
      WS_CONFIG.heartbeat.timeout
    );
  }, []);

  const getLastActivity = useCallback(() => lastActivityRef.current, []);

  const start = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    updateActivity();

    intervalRef.current = window.setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          const pingMessage: WebSocketMessage<unknown> = {
            action: WebSocketAction.PING,
          };
          wsRef.current.send(JSON.stringify(pingMessage));

          if (isStale()) {
            log.warn('Connection stale, triggering reconnect');
            onStaleConnection(lastActivityRef.current);
          }
        } catch (error) {
          log.error('Heartbeat error:', error);
          onStaleConnection(lastActivityRef.current);
        }
      }
    }, WS_CONFIG.heartbeat.interval);
  }, [wsRef, updateActivity, isStale, onStaleConnection]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return stop;
  }, [stop]);

  return useMemo(
    () => ({ start, stop, updateActivity, isStale, getLastActivity }),
    [start, stop, updateActivity, isStale, getLastActivity],
  );
}

// Props interface
interface WebSocketProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export function WebSocketProvider({
  children,
  enabled = true,
}: WebSocketProviderProps) {
  // Authentication
  const { getToken } = useAuth();

  // Connection state
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.DISCONNECTED,
  );

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastDisconnectTimeRef = useRef<number | null>(null);
  const staleSinceRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);
  const isConnectingRef = useRef(false);
  const eventEmitterRef = useRef(new EventEmitter());

  const isEmailAccountRemoved = useCallback(
    (platform: MessagingPlatform, address: string) =>
      useIntegrationsStore.getState().isEmailAccountRemoved(platform, address),
    [],
  );

  // Custom hooks
  const reconnectStrategy = useReconnectStrategy();

  // Create message handlers using extracted factory functions
  const handleUserCommunicationMessage = useMemo(
    () =>
      createUserCommunicationMessageHandler(
        eventEmitterRef.current,
        isEmailAccountRemoved,
      ),
    [isEmailAccountRemoved],
  );

  const handleIntegrationAccountMessage = useMemo(
    () => createIntegrationAccountMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleInboxEntryMessage = useMemo(
    () => createInboxEntryMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleInboxEntryReplyStateMessage = useMemo(
    () => createInboxEntryReplyStateMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleInboxEntryUnreadMessage = useMemo(
    () => createInboxEntryUnreadMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleNotificationMessage = useMemo(
    () => createNotificationMessageHandler(eventEmitterRef.current),
    [],
  );

  const handlePersonActivityMessage = useMemo(
    () => createPersonActivityMessageHandler(eventEmitterRef.current),
    [],
  );

  const handlePersonMessage = useMemo(
    () => createPersonMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleFlowGenerationJobMessage = useMemo(
    () => createFlowGenerationJobMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleAgentEventMessage = useMemo(
    () => createAgentEventMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleKnowledgeSpaceMessage = useMemo(
    () => createKnowledgeSpaceMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleKnowledgePageMessage = useMemo(
    () => createKnowledgePageMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleKnowledgeFavoriteMessage = useMemo(
    () => createKnowledgeFavoriteMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleDoBoxRuleDecisionMessage = useMemo(
    () => createDoBoxRuleDecisionMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleLocalDevicePresenceMessage = useMemo(
    () => createLocalDevicePresenceMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleTaskGroupMessage = useMemo(
    () => createTaskGroupMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleTaskLabelMessage = useMemo(
    () => createTaskLabelMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleTaskSubgroupMessage = useMemo(
    () => createTaskSubgroupMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleTeamMessage = useMemo(
    () => createTeamMessageHandler(eventEmitterRef.current),
    [],
  );

  const handleTeamInviteMessage = useMemo(
    () => createTeamInviteMessageHandler(eventEmitterRef.current),
    [],
  );

  // Memoized handlers
  const handleStaleConnection = useCallback(
    (lastActivity: number) => {
      staleSinceRef.current = lastActivity;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Cancel any scheduled reconnect before setting new state
      reconnectStrategy.reset();
      setConnectionState(ConnectionState.RECONNECTING);
    },
    [reconnectStrategy],
  );

  const heartbeat = useHeartbeat(wsRef, handleStaleConnection);

  // Optimized message handler
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      heartbeat.updateActivity();

      try {
        const data: WebSocketMessage<unknown> = JSON.parse(event.data);

        if (data?.action === WebSocketAction.PONG) {
          return;
        }

        if (!data?.action || !data?.entityType || !data?.item) {
          log.warn(`Invalid message format: ${JSON.stringify(data)}`);
          return;
        }

        const { action, entityType, item, reopened, previous, alreadyDeleted } =
          data;

        // Route to appropriate entity handler
        switch (entityType) {
          case WebSocketEntityType.UserCommunication:
            handleUserCommunicationMessage(
              action,
              item as
                | UserCommunication
                | UserCommunicationRemovePayload
                | UserCommunicationRemoveBatchPayload,
            );
            break;
          case WebSocketEntityType.IntegrationAccount:
            handleIntegrationAccountMessage(
              action,
              item as IntegrationAccountRemovedPayload,
            );
            break;
          case WebSocketEntityType.InboxEntry:
            handleInboxEntryMessage(action, item as InboxEntry, {
              reopened,
              previous,
              alreadyDeleted,
            });
            break;
          case WebSocketEntityType.InboxEntryReplyState:
            handleInboxEntryReplyStateMessage(
              action,
              item as InboxEntryReplyStatePayload,
            );
            break;
          case WebSocketEntityType.InboxEntryUnread:
            handleInboxEntryUnreadMessage(action, item as InboxEntryUnread);
            break;
          case WebSocketEntityType.Notification:
            handleNotificationMessage(action, item as Notification);
            break;
          case WebSocketEntityType.FlowGenerationJob:
            handleFlowGenerationJobMessage(
              action,
              item as FlowGenerationJobResult,
            );
            break;
          case WebSocketEntityType.AgentEvent:
            handleAgentEventMessage(action, item as AgentEventPayload);
            break;
          case WebSocketEntityType.KnowledgeSpace:
            handleKnowledgeSpaceMessage(action, item as KnowledgeSpace);
            break;
          case WebSocketEntityType.KnowledgePage:
            handleKnowledgePageMessage(action, item as KnowledgePage);
            break;
          case WebSocketEntityType.KnowledgeFavorite:
            handleKnowledgeFavoriteMessage(
              action,
              item as KnowledgeFavoritePayload,
            );
            break;
          case WebSocketEntityType.DoBoxRule:
            handleDoBoxRuleDecisionMessage(action, item as DoBoxRuleDecision);
            break;
          case WebSocketEntityType.TaskGroup:
            handleTaskGroupMessage(action, item as TaskGroup);
            break;
          case WebSocketEntityType.TaskLabel:
            handleTaskLabelMessage(action, item as TaskLabel);
            break;
          case WebSocketEntityType.TaskSubgroup:
            handleTaskSubgroupMessage(action, item as TaskSubgroup);
            break;
          case WebSocketEntityType.PersonActivity:
            handlePersonActivityMessage(
              action,
              item as
                | PersonActivityMigratedPayload
                | PersonActivityCreatedPayload
                | PersonActivityRemovedPayload,
            );
            break;
          case WebSocketEntityType.Person:
            handlePersonMessage(action, item as TeamPersonPayload);
            break;
          case WebSocketEntityType.Team:
            handleTeamMessage(action, item as Team);
            break;
          case WebSocketEntityType.TeamInvite:
            handleTeamInviteMessage(action, item as TeamInvite);
            break;
          case WebSocketEntityType.LocalDevicePresence:
            handleLocalDevicePresenceMessage(
              action,
              item as LocalDevicePresencePayload,
            );
            break;
          default:
            // Not an anomaly on the wire — a client with no route for it. Stays
            // non-fatal so a backend deployed ahead of the web app is still
            // forward-compatible; WebSocketProvider.routing.test.ts is what
            // fails when a route is missing by mistake.
            log.warn(`No client route for entityType: ${entityType}`);
        }
      } catch (error) {
        log.error('Message parsing error:', error);
      }
    },
    [
      heartbeat,
      handleUserCommunicationMessage,
      handleIntegrationAccountMessage,
      handleInboxEntryMessage,
      handleInboxEntryReplyStateMessage,
      handleInboxEntryUnreadMessage,
      handleNotificationMessage,
      handleFlowGenerationJobMessage,
      handleAgentEventMessage,
      handleKnowledgeSpaceMessage,
      handleKnowledgePageMessage,
      handleKnowledgeFavoriteMessage,
      handleDoBoxRuleDecisionMessage,
      handleTaskGroupMessage,
      handleTaskLabelMessage,
      handleTaskSubgroupMessage,
      handlePersonActivityMessage,
      handlePersonMessage,
      handleTeamMessage,
      handleTeamInviteMessage,
      handleLocalDevicePresenceMessage,
    ],
  );

  // Optimized cleanup function
  const cleanup = useCallback(() => {
    heartbeat.stop();
    reconnectStrategy.reset();
    isConnectingRef.current = false;

    // Clear connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    // Abort any pending operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;

      // Remove listeners before closing
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;

      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close(1000, 'Manual disconnect');
      }
    }

    setConnectionState(ConnectionState.DISCONNECTED);
    lastDisconnectTimeRef.current = getCurrentTimestamp();
  }, [heartbeat, reconnectStrategy]);

  // Optimized connection function
  const connect = useCallback(async () => {
    if (!enabled) {
      return;
    }

    if (!navigator.onLine) {
      setConnectionState(ConnectionState.DISCONNECTED);
      return;
    }

    // Prevent race conditions - only one connection attempt at a time
    if (isConnectingRef.current) {
      return;
    }

    // Prevent multiple simultaneous connections
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.CONNECTING ||
        wsRef.current.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    if (!WS_URL) {
      log.error(
        'VITE_WEBSOCKET_BASE_URL is not configured; WebSocket disabled',
      );
      return;
    }

    // Mark connection attempt in progress
    isConnectingRef.current = true;

    // Create new abort controller for this connection attempt
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Check if was offline for a long time
    const wasLongPause = shouldResyncAfterReconnect(
      lastDisconnectTimeRef.current,
      staleSinceRef.current,
      getCurrentTimestamp(),
      WS_CONFIG.longPauseThreshold,
    );

    setConnectionState(ConnectionState.CONNECTING);

    try {
      const token = await getToken();

      // Check if connection was aborted during async operation
      if (abortController.signal.aborted) {
        return;
      }

      if (!token) {
        throw new Error('No authentication token available');
      }

      const ws = new WebSocket(
        `${WS_CONFIG.url}?token=${encodeURIComponent(token)}`,
      );

      // Timeout for initial connection
      connectionTimeoutRef.current = window.setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          log.error('Connection timeout');
          ws.close();
        }
      }, WS_CONFIG.connectionTimeout);

      ws.onopen = () => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

        if (abortController.signal.aborted) {
          isConnectingRef.current = false;
          ws.close();
          return;
        }

        log.info('WebSocket connected');
        wsRef.current = ws;
        setConnectionState(ConnectionState.CONNECTED);
        reconnectStrategy.reset();
        heartbeat.start();
        isConnectingRef.current = false;

        if (wasLongPause) {
          log.info('Reconnected after long pause');
          eventEmitterRef.current.emit(
            WebSocketEntityType.UserCommunication,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.InboxEntry,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.Notification,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.AgentEvent,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.TaskGroup,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.TaskLabel,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.TaskSubgroup,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.InboxEntryUnread,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.KnowledgeSpace,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.KnowledgePage,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
          eventEmitterRef.current.emit(
            WebSocketEntityType.KnowledgeFavorite,
            WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
          );
        }

        // reset baseline now that we're freshly connected
        staleSinceRef.current = null;
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        log.error('WebSocket error:', error);
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

        if (abortController.signal.aborted) {
          isConnectingRef.current = false;
          return;
        }

        log.info(`WebSocket closed: ${event.code} - ${event.reason}`);
        wsRef.current = null;
        heartbeat.stop();
        isConnectingRef.current = false;

        // Only reconnect if not a clean close and enabled
        if (enabled && event.code !== 1000 && event.code !== 1001) {
          setConnectionState(ConnectionState.RECONNECTING);
          reconnectStrategy.scheduleNext(() => connect());
        } else {
          setConnectionState(ConnectionState.DISCONNECTED);
        }

        lastDisconnectTimeRef.current = getCurrentTimestamp();
      };
    } catch (error) {
      if (abortController.signal.aborted) {
        isConnectingRef.current = false;
        return;
      }

      log.error('Connection error:', error);
      isConnectingRef.current = false;

      if (enabled) {
        setConnectionState(ConnectionState.RECONNECTING);
        reconnectStrategy.scheduleNext(() => connect());
      } else {
        setConnectionState(ConnectionState.DISCONNECTED);
      }
    }
  }, [enabled, handleMessage, heartbeat, reconnectStrategy]);

  // Stable references for effects to prevent unnecessary re-execution
  const connectStable = useEventCallback(connect);
  const cleanupStable = useEventCallback(cleanup);

  // Reconnect when going online
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleOnline = () => {
      log.info('Network online, checking connection');

      reconnectStrategy.reset();
      if (
        connectionState === ConnectionState.DISCONNECTED ||
        connectionState === ConnectionState.RECONNECTING
      ) {
        connectStable();
      }
    };

    const handleOffline = () => {
      log.info('Network offline');
      // Don't attempt to reconnect while offline
      cleanupStable();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [
    enabled,
    connectionState,
    connectStable,
    cleanupStable,
    reconnectStrategy,
  ]);

  // Reconnect when returning to tab
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (connectionState === ConnectionState.DISCONNECTED) {
          connectStable();
        } else if (connectionState === ConnectionState.RECONNECTING) {
          // If already reconnecting, reset the strategy to try immediately
          reconnectStrategy.reset();
          connectStable();
        } else if (
          connectionState === ConnectionState.CONNECTED &&
          heartbeat.isStale()
        ) {
          log.info('Connection stale after tab switch');
          // Set before cleanup and cleared only on successful open — cleanup() must never touch staleSinceRef or a failed reconnect would swallow the pending resync.
          staleSinceRef.current = heartbeat.getLastActivity();
          cleanupStable();
          connectStable();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    enabled,
    connectionState,
    heartbeat,
    connectStable,
    cleanupStable,
    reconnectStrategy,
  ]);

  // Initial connection and cleanup
  useEffect(() => {
    if (enabled) {
      connectStable();
    }

    return () => {
      cleanupStable();
      eventEmitterRef.current.clear();
    };
  }, [enabled, connectStable, cleanupStable]);

  // Memoized subscribe function
  const subscribe = useCallback(
    <T,>(
      entityType: WebSocketEntityType,
      eventType: WebSocketEventType | WebSocketEventType[],
      callback: WebSocketEventCallback<T>,
    ) => {
      return eventEmitterRef.current.subscribe(entityType, eventType, callback);
    },
    [],
  );

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      reconnect: connect,
      disconnect: cleanup,
      connectionState,
      isConnected: connectionState === ConnectionState.CONNECTED,
      subscribe,
    }),
    [connect, cleanup, connectionState, subscribe],
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}
